import { useEffect } from "react";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import RequireAdmin from "@/components/admin/RequireAdmin";
import AdminLogin from "@/pages/admin/AdminLogin";
import LeadCentre from "@/pages/admin/LeadCentre";
import LeadProfile from "@/pages/admin/LeadProfile";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import ScrollToTop from "@/components/site/ScrollToTop";
import MetaPixelTracker from "@/components/site/MetaPixelTracker";
import Home from "@/pages/Home";
import ServicesPage from "@/pages/ServicesPage";
import LandingPage from "@/pages/LandingPage";
import { SERVICE_PAGES, INDUSTRY_PAGES } from "@/content/landings";
import PortalPage from "@/pages/PortalPage";
import IndustriesPage from "@/pages/IndustriesPage";
import AboutPage from "@/pages/AboutPage";
import EnquirePage from "@/pages/EnquirePage";
import WorkPage from "@/pages/WorkPage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import { CASE_STUDY_PAGES } from "@/content/caseStudies";
import NotFound from "@/pages/NotFound";
import MediaManager from "@/pages/admin/MediaManager";
import { MediaProvider } from "@/context/MediaContext";

const Layout = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App grain">
      <ScrollToTop />
      <Nav />
      <main>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Toaster position="bottom-right" richColors />
      <AuthProvider>
      <MediaProvider>
        <MetaPixelTracker />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<ServicesPage />} />
            {SERVICE_PAGES.map((p) => <Route key={p.path} path={p.path} element={<LandingPage page={p} kind="service" />} />)}
            {INDUSTRY_PAGES.map((p) => <Route key={p.path} path={p.path} element={<LandingPage page={p} kind="industry" />} />)}
            <Route path="/services/content-production" element={<Navigate to="/content-creation-sydney" replace />} />
            <Route path="/services/social-media-management" element={<Navigate to="/social-media-marketing-sydney" replace />} />
            <Route path="/services/paid-advertising" element={<Navigate to="/meta-ads-agency-sydney" replace />} />
            <Route path="/services/websites" element={<Navigate to="/website-design-sydney" replace />} />
            <Route path="/services/crm-ai-systems" element={<Navigate to="/custom-business-systems" replace />} />
            <Route path="/services/automation" element={<Navigate to="/custom-business-systems" replace />} />
            <Route path="/wholesale-marketing" element={<Navigate to="/food-wholesaler-marketing" replace />} />
            <Route path="/packaging-marketing" element={<Navigate to="/packaging-marketing-agency" replace />} />
            {CASE_STUDY_PAGES.map((c) => <Route key={c.path} path={c.path} element={<CaseStudyPage study={c} />} />)}
            <Route path="*" element={<NotFound />} />
            <Route path="/portal" element={<PortalPage />} />
            <Route path="/industries" element={<IndustriesPage />} />
            <Route path="/work" element={<WorkPage />} />
            <Route path="/process" element={<Navigate to="/#method" replace />} />
            <Route path="/about" element={<AboutPage />} />
          </Route>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/enquire" element={<EnquirePage />} />
          <Route path="/admin" element={<RequireAdmin><LeadCentre /></RequireAdmin>} />
          <Route path="/admin/media" element={<RequireAdmin><MediaManager /></RequireAdmin>} />
          <Route path="/admin/leads/:id" element={<RequireAdmin><LeadProfile /></RequireAdmin>} />
        </Routes>
      </MediaProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
